// In-memory store for Invitation - External (DMUT).
//
// TODO(API): stands in for the backend until the external-invitation contract
// exists. Mirrors hpsStore / procurementStore so the swap is mechanical; callers
// go through invitationExternalApi.js rather than importing this directly.
//
// Only two mutations exist (Accept / Decline) because the DMUT-facing screens are
// otherwise read-only.

import { SEED_INVITATIONS } from './invitationExternalDummyData';
import {
  INVITATION_STATUS,
  SEARCHABLE_FIELDS,
  SORTABLE_FIELDS,
} from './invitationExternalConstants';

const clone = obj => JSON.parse(JSON.stringify(obj));

let records = clone(SEED_INVITATIONS);

// Expired is derived, never stored: an invitation left at Open once its
// Submission Deadline has passed is Expired ("jika sampai waktu validity
// submission berakhir tidak di Accept atau di Reject"). Accepted / Declined are
// terminal and keep their stored status even after the deadline — the DMUT did
// respond in time.
//
// Exported because the Accept/Decline guards must agree with what the list shows;
// deciding "can respond" from the stored status alone would let a DMUT accept an
// invitation the list already reports as Expired.
export const deriveStatus = (record, now = Date.now()) => {
  if (record.invitationStatus !== INVITATION_STATUS.OPEN) {
    return record.invitationStatus;
  }
  const deadline = record.requirement?.validity?.submissionDeadline;
  if (!deadline) return INVITATION_STATUS.OPEN;
  const parsed = new Date(deadline).getTime();
  if (Number.isNaN(parsed)) return INVITATION_STATUS.OPEN;
  return parsed < now ? INVITATION_STATUS.EXPIRED : INVITATION_STATUS.OPEN;
};

// Project a stored record into what the UI reads: stored fields plus the derived
// status. Everything downstream sees one consistent `invitationStatus`.
const project = (record, now) => ({
  ...clone(record),
  invitationStatus: deriveStatus(record, now),
});

// Keyword search — Title, Id Procurement and Description only (DOD item 2).
const matchesSearch = (r, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return SEARCHABLE_FIELDS.some(f =>
    String(r[f] || '')
      .toLowerCase()
      .includes(needle)
  );
};

const getSortValue = (r, field) =>
  SORTABLE_FIELDS.includes(field) ? r[field] || '' : '';

export const list = ({
  search = '',
  page = 1,
  pageSize = 10,
  sortField = null,
  sortDir = 'asc',
  now = Date.now(),
} = {}) => {
  // Project before filtering/sorting so a search or sort on Invitation Status
  // sees the derived value, matching what the column renders.
  let filtered = records
    .map(r => project(r, now))
    .filter(r => matchesSearch(r, search));

  if (sortField) {
    filtered = [...filtered].sort((a, b) => {
      const cmp = String(getSortValue(a, sortField)).localeCompare(
        String(getSortValue(b, sortField)),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const totalCount = filtered.length;
  const start = (page - 1) * pageSize;
  return { items: filtered.slice(start, start + pageSize), totalCount };
};

// Single record for the Detail page. Returns null when the id is unknown so the
// caller can render its not-found panel.
export const getById = (id, { now = Date.now() } = {}) => {
  const found = records.find(r => r.id === id);
  return found ? project(found, now) : null;
};

// Guard shared by both mutations: the invitation must still be genuinely Open.
// Rejecting here (rather than only hiding the buttons) keeps a stale page from
// responding to an invitation that expired while it sat open in the browser.
const assertRespondable = (id, now) => {
  const record = records.find(r => r.id === id);
  if (!record) throw new Error('Invitation not found');
  const status = deriveStatus(record, now);
  if (status !== INVITATION_STATUS.OPEN) {
    throw new Error(
      `This invitation can no longer be responded to (status: ${status})`
    );
  }
  return record;
};

// Accept — the DOD also says the Offer status becomes "Open" afterwards. No Offer
// module reads this yet, so the transition is recorded on the record instead of
// reaching into another module's state.
export const accept = (id, { now = Date.now() } = {}) => {
  const record = assertRespondable(id, now);
  record.invitationStatus = INVITATION_STATUS.ACCEPTED;
  record.respondedAt = new Date(now).toISOString();
  record.declineReason = null;
  record.offerStatus = 'Open';
  return project(record, now);
};

// Decline — the reason is mandatory ("mengisi alasan decline sebelum submit").
export const decline = (id, reason, { now = Date.now() } = {}) => {
  const trimmed = String(reason || '').trim();
  if (!trimmed) throw new Error('A decline reason is required');
  const record = assertRespondable(id, now);
  record.invitationStatus = INVITATION_STATUS.DECLINED;
  record.respondedAt = new Date(now).toISOString();
  record.declineReason = trimmed;
  record.offerStatus = null;
  return project(record, now);
};

// Test/util hook — reset back to the seed set.
export const _reset = () => {
  records = clone(SEED_INVITATIONS);
};
